import { IMenuItem, IMenuItemType, ISideMenuItem } from "shared/components/SideMenu/types";
import { VENUE_PATHS } from "Venues/router";
import { buildUrl, IUserPermissions } from "shared/utils";
import { Stage } from "shared/assets/icons";
import { copy } from "shared/assets";
import {
  getEquipmentBlockActions,
  infoBlockList,
  getInfoBlockActions,
  VenueRouteParams,
} from "Venues/utils";
import { IEquipmentCategoryItem, IInfoBlockType, IVenueBaseInfoBlocks } from "Venues/types";
import { SuggestEditFormType } from "Venues/components";

const { createStage } = copy;

interface VenueAddMenuItemsProps {
  blocks: IVenueBaseInfoBlocks;
  getPath: (path: string, options?: VenueRouteParams) => string;
}

export const getVenueAddMenuItems = ({
  blocks,
  getPath,
}: VenueAddMenuItemsProps): ISideMenuItem[] => [
  {
    id: "createStage",
    title: createStage.stage,
    path: getPath(VENUE_PATHS.CREATE_VENUE_STAGE),
    type: IMenuItemType.add,
    icon: Stage,
  },
  ...getNotAddedInfoBlockItems({
    getPath: (infoBlockSlug: string, removeQueryParams?: boolean) =>
      getPath(VENUE_PATHS.EDIT_VENUE_INFO_BLOCK, {
        params: { infoBlockSlug },
        removeQueryParams,
      }),
    blocks,
  }),
];

interface StageAddMenuItemsProps {
  stageId: string;
  blocks: IVenueBaseInfoBlocks;
  getPath: (path: string, options?: VenueRouteParams) => string;
}

export const getStageAddMenuItems = ({
  stageId,
  blocks,
  getPath,
}: StageAddMenuItemsProps): ISideMenuItem[] =>
  getNotAddedInfoBlockItems({
    getPath: (infoBlockSlug: string, removeQueryParams?: boolean) =>
      getPath(VENUE_PATHS.EDIT_STAGE_INFO_BLOCK, {
        stageId,
        params: { infoBlockSlug },
        removeQueryParams,
      }),
    blocks,
  });

interface StageSubItemsProps {
  venueId: string;
  stageId: string;
  blocks: IVenueBaseInfoBlocks;
  equipmentCategories: IEquipmentCategoryItem[];
  userPermissions: IUserPermissions;
  handleEdit: (infoBlockSlug: string) => void;
  handleDelete: ({ id, type }: { id: string; type: IInfoBlockType }) => void;
  openSuggestStageEditModal: (props: { type: SuggestEditFormType; stageId: string }) => void;
  getPath: (path: string, options?: VenueRouteParams) => string;
  pushRoute: (path: string, options?: VenueRouteParams) => void;
}

export const getStageSubItems = ({
  venueId,
  stageId,
  blocks,
  equipmentCategories,
  userPermissions,
  handleEdit,
  handleDelete,
  openSuggestStageEditModal,
  getPath,
  pushRoute,
}: StageSubItemsProps): IMenuItem[] => [
  ...getAddedInfoBlockItems({
    getPath: (infoBlockSlug: string, removeQueryParams?: boolean) =>
      getPath(VENUE_PATHS.STAGE, {
        venueId,
        stageId,
        hash: infoBlockSlug,
        removeQueryParams,
      }),
    getEditPath: (infoBlockSlug: string, removeQueryParams?: boolean) =>
      getPath(VENUE_PATHS.EDIT_STAGE_INFO_BLOCK, {
        venueId,
        stageId,
        params: { infoBlockSlug },
        removeQueryParams,
      }),
    blocks,
    userPermissions,
    handleEdit,
    handleDelete,
    openSuggestEditModal: props =>
      openSuggestStageEditModal({ ...props, type: "StageInfoBlock", stageId }),
  }),
  ...equipmentCategories.map(({ category, label: title, slug: categorySlug }) => ({
    id: category,
    title,
    path: getPath(VENUE_PATHS.STAGE, {
      venueId,
      stageId,
      hash: categorySlug,
    }),
    matchPaths: [
      buildUrl(
        VENUE_PATHS.STAGE,
        {
          venueId,
          stageId,
        },
        categorySlug
      ),
      buildUrl(VENUE_PATHS.EDIT_STAGE_EQUIPMENT, {
        venueId,
        stageId,
        categorySlug,
      }),
    ],
    actions: getEquipmentBlockActions({
      userPermissions,
      venueId,
      stageId,
      categorySlug,
      openSuggestStageEditModal: openSuggestStageEditModal,
      pushRoute,
    }),
  })),
];

interface AddInfoBlockProps {
  getPath: (infoBlockSlug: string, removeQueryParams?: boolean) => string;
  blocks: IVenueBaseInfoBlocks;
}

interface EditInfoBlockProps {
  getPath: (infoBlockSlug: string, removeQueryParams?: boolean) => string;
  getEditPath: (infoBlockSlug: string, removeQueryParams?: boolean) => string;
  blocks: IVenueBaseInfoBlocks;
  userPermissions: IUserPermissions;
  handleEdit: (infoBlockSlug: string) => void;
  handleDelete: ({ id, type }: { id: string; type: IInfoBlockType }) => void;
  openSuggestEditModal: (props: { infoBlockSlug: string; stageId?: string }) => void;
}

export const getAddedInfoBlockItems = ({
  blocks,
  getPath,
  getEditPath,
  userPermissions,
  handleDelete,
  handleEdit,
  openSuggestEditModal,
}: EditInfoBlockProps) =>
  infoBlockList.reduce((items: ISideMenuItem[], { type, label, subItems }) => {
    const addedSubItems = subItems.filter(({ type }) => type in blocks && blocks[type] !== null);
    if (addedSubItems.length === 0) {
      return items;
    }

    return [
      ...items,
      {
        id: `menu_item_${getPath(type)}`,
        title: label,
        type: IMenuItemType.default,
        matchPaths: addedSubItems
          .map(subItem => [getEditPath(subItem.type, true), getPath(subItem.type, true)])
          .flat(),
        expandableSubItems: true,
        subItems: addedSubItems.map(subItem => ({
          id: `menu_item_${subItem.type}`,
          title: subItem.label,
          path: getPath(subItem.type),
          matchPaths: [getPath(subItem.type, true), getEditPath(subItem.type, true)],
          actions: getInfoBlockActions({
            userPermissions: userPermissions,
            handleEdit: () => handleEdit(subItem.type),
            handleDelete: () =>
              handleDelete({
                id: blocks[subItem.type]?.id as string,
                type: subItem.type,
              }),
            openSuggestEditModal: () =>
              openSuggestEditModal({
                infoBlockSlug: subItem.type,
              }),
          }),
        })),
      } as ISideMenuItem,
    ];
  }, []);

export const getNotAddedInfoBlockItems = ({ blocks, getPath }: AddInfoBlockProps) =>
  infoBlockList.reduce((items: ISideMenuItem[], { type, label, subItems }) => {
    const notAddedSubItems = subItems.filter(
      ({ type }) => !(type in blocks) || blocks[type] === null
    );

    if (notAddedSubItems.length === 0) {
      return items;
    }
    return [
      ...items,
      {
        id: `add_menu_item_${type}`,
        title: label,
        type: IMenuItemType.add,
        expandableSubItems: true,
        subItems: notAddedSubItems.map(subItem => ({
          id: `menu_item_${subItem.type}`,
          title: subItem.label,
          path: getPath(subItem.type),
          type: IMenuItemType.add,
        })),
      } as ISideMenuItem,
    ];
  }, []);
